import { useCallback } from 'react';
import { useUserStatus } from '../context/user-status-context';
import { EventBus } from '@/shared/utils';
import { CatalogEvents, CatalogEventKeys } from '../events';

interface UseAuthFeaturesOptions {
  eventBus?: EventBus<CatalogEvents>;
}

/**
 * Hook that provides authenticated features for catalog components.
 * Built on top of the UserStatus context with additional catalog-specific logic.
 */
export function useAuthFeatures(options?: UseAuthFeaturesOptions) {
  const { eventBus } = options || {};
  const {
    userStatus,
    isLoggedIn,
    isSubscribed,
    canAccessContent,
    canAddFavorites,
    canWatchVideos,
    getRedirectUrlForAction,
    getCourseAccess,
  } = useUserStatus();

  // Trigger navigation based on auth status
  const handleAuthAction = useCallback(
    (actionType: 'watch' | 'favorite', originalUrl: string, fallbackAction?: () => void) => {
      // Check if the user has the required permissions
      let hasPermission = false;

      if (actionType === 'watch') {
        hasPermission = canWatchVideos();
      } else if (actionType === 'favorite') {
        hasPermission = canAddFavorites();
      }

      if (!hasPermission) {
        // User doesn't have permission, redirect
        const redirectUrl = getRedirectUrlForAction(actionType, originalUrl);

        if (eventBus) {
          eventBus.emit(CatalogEventKeys.redirects.to, redirectUrl);
        } else if (typeof window !== 'undefined') {
          window.location.href = redirectUrl;
        }
        return false;
      }

      // User has permission, proceed with the action
      if (fallbackAction) {
        fallbackAction();
      }
      return true;
    },
    [canWatchVideos, canAddFavorites, getRedirectUrlForAction, eventBus],
  );

  // Get appropriate text for buttons based on auth status
  const getActionButtonText = useCallback(
    (actionType: 'watch' | 'favorite') => {
      if (!isLoggedIn) {
        return actionType === 'watch' ? 'Accedi per guardare' : 'Accedi per salvare';
      }

      if (actionType === 'watch' && !canWatchVideos()) {
        return 'Abbonati per guardare';
      }

      if (actionType === 'favorite') {
        return 'Aggiungi ai preferiti';
      }

      return 'Guarda ora';
    },
    [isLoggedIn, canWatchVideos],
  );

  return {
    userStatus,
    isLoggedIn,
    isSubscribed,
    canAccessContent,
    canAddFavorites,
    canWatchVideos,
    getRedirectUrlForAction,
    getCourseAccess,
    handleAuthAction,
    getActionButtonText,

    // Convenience computed properties
    isGuest: userStatus === 'guest',
    isLoggedUser: userStatus === 'logged',
    isSubscribedUser: userStatus === 'subscribed',
  };
}
